import type { FetchContext } from 'ofetch';
import { ofetch } from 'ofetch';
import type { FetchOptions } from 'ofetch';
import { deepConvertKey } from '~/src/utils/lodashExtend';
import { snakeCase } from 'lodash-es';
import type { CustomFetchContext } from './types';
import { GLOBAL_CONFIG } from '~/configs/environment/environment';
import { ApiResponse } from '../types';
import { getRuntimeContext } from '~/server/helpers/server';
import { APIEventEmitter } from '~/src/services/event';
import { EventNameMap } from '~/src/services/event/constants';
import { API_STATUS_CODE_SERVER_ERROR, API_STATUS_CODE_UN_LOGIN } from './constants';
import { StorageKeyEnum } from '@/src/constants/user';

export interface OFetchRequestOptions extends FetchOptions {
  // 使用x-async-request 字段
  useHeaderAsyncRequest?: boolean;
  // 使用X-Requested-With 字段
  isSkipXRequestWith?: boolean;
  // 使用小程序的subjectId 作为header
  useHeaderMiniProgramSubjectId?: boolean;
  useAbort?: boolean;
  useApiLog?: boolean;
  // 跳过错误校验
  isSkipError?: boolean;
  // 跳过请求数据体转换
  isSkipConvertRequest?: boolean;
  // 跳过响应数据体转换
  isSkipConvertResponse?: boolean;
  // 跳过响应体解析
  isSkipResponseParse?: boolean;
  // 跳过状态码校验
  isSkipStatusCheck?: boolean;
}

const baseURL = GLOBAL_CONFIG.envConfig.origin + '/napi';
const oFetch = ofetch.create({
  baseURL,
  retry: 2,
  retryDelay: 500,
  timeout: 10000,
});

export const useOFetch = <T>(
  url: string,
  customOptions: OFetchRequestOptions,
  customFetchContext?: CustomFetchContext
): Promise<ApiResponse<T>> => {
  const $logger = customFetchContext?.nuxtApp?.$logger;
  const defaultHeaders = {} as any;

  // 请求参数默认进行命名转换
  if (customOptions.isSkipConvertRequest !== false) {
    customOptions.query = deepConvertKey(customOptions.query, snakeCase);
    customOptions.body = deepConvertKey(customOptions.body, snakeCase);
  }

  if (process.server && customFetchContext?.nuxtApp) {
    const nuxtApp = customFetchContext.nuxtApp;
    const context = getRuntimeContext(nuxtApp.ssrContext!.event!);

    if (context.cookie) {
      defaultHeaders.cookie = context.cookie;
    }
  }

  // 默认标记异步请求；外站异步请求请使用skip
  if (customOptions.isSkipXRequestWith !== true) {
    defaultHeaders['X-Requested-With'] = 'XMLHttpRequest';
  }

  if (customOptions.useHeaderAsyncRequest === true) {
    defaultHeaders['X-Async-Request'] = true;
  }

  if (customOptions.useHeaderMiniProgramSubjectId === true) {
    if (process.client) {
      const userSubject = window.localStorage.getItem(StorageKeyEnum.HOST_PROGRAM_SUBJECT_ID);
      userSubject && (defaultHeaders['user-subject'] = userSubject);
    }
  }

  Object.assign(defaultHeaders, customOptions.headers || {});

  const useApiLog = customOptions.useApiLog !== false;

  const fetchOptions: OFetchRequestOptions = {
    headers: defaultHeaders,
    parseResponse: customOptions.isSkipResponseParse
      ? undefined
      : (responseText: string) => {
          let result = responseText;

          try {
            result = JSON.parse(responseText);
          } catch (err) {
            if (process.server && useApiLog && $logger && err) {
              $logger.info('[OFetch parse error:]', responseText);
            }

            result = responseText;
          }

          return result;
        },
    onRequest({ request, options }: FetchContext) {
      options.headers = (options.headers || {}) as { [key: string]: string };

      if (process.server && $logger) {
        $logger.info('[API onRequest:]', options);
      }
    },
    onRequestError({ request, options, error }: FetchContext) {
      // Log error
      if (process.server && $logger) {
        $logger.error('[API onRequestError:]', JSON.stringify(options), error);
      }
    },
    onResponse({ request, response, options }: FetchContext) {
      if (typeof response === 'string') {
        return;
      }

      if (customOptions.isSkipConvertResponse !== false) {
        if (response && response._data) {
          response._data = deepConvertKey(response._data);
        }
      }

      if (process.server && $logger) {
        console.log('\n[API: ====================start=========================== ]\n');
        console.log('[API request:]', request, '\n');
        console.log('[API options:]', JSON.stringify(options), '\n');
        console.log(`[API response:]`, JSON.stringify(response?._data || {}), '\n');
        console.log('[API: =====================end======================== ]', '\n');

        $logger.info('[API response:]', {
          opts: options,
          req: request,
          res: JSON.stringify(response?._data || {}),
        });
      }

      // error api status.
      if (customOptions.isSkipStatusCheck !== true) {
        const status = response?._data?.status;

        if (API_STATUS_CODE_UN_LOGIN.includes(status)) {
          APIEventEmitter.emit(EventNameMap.API_SERVER_ERROR, response);
          APIEventEmitter.emit(EventNameMap.API_UN_LOGIN, response);

          return Promise.reject(response);
        }

        if (!API_STATUS_CODE_SERVER_ERROR.includes(status)) {
          APIEventEmitter.emit(EventNameMap.API_SERVER_ERROR, response);

          return Promise.reject(response);
        }
      }
    },
    onResponseError({ request, response, options, error }: FetchContext) {
      if (process.server && $logger) {
        try {
          console.error(
            '[API responseError: ====================start=========================== ]'
          );
          console.error('[API error response:]', JSON.stringify(response));
          console.error('[API error info:]', JSON.stringify(error));
          console.error('[API responseError: ====================end=========================== ]');

          $logger.error('[API error response:]', {
            opts: options,
            req: request,
            err: error,
            res: JSON.stringify(response?._data || {}),
          });
        } catch (err) {
          console.error('ofetch print log error.');
        }
      }

      if (customOptions.isSkipError !== false) {
        process.client &&
          console.error(
            Array.isArray(response!._data.message)
              ? response!._data.message[0]
              : response!._data.message
          );

        process.server &&
          $logger &&
          $logger.error(
            Array.isArray(response!._data.message)
              ? response!._data.message[0]
              : response!._data.message
          );
      }

      const statusCode = response?.status;

      if (![200].includes(statusCode!)) {
        APIEventEmitter.emit(EventNameMap.API_SERVER_ERROR, response);

        return Promise.reject(response);
      }
    },
    ...customOptions,
  };

  // 调用 controller.abort(); 即可终止请求
  if (customOptions?.useAbort && customFetchContext?.controller !== undefined) {
    // 接受abortController形参，如果不存在，则创建一个并赋值
    customFetchContext.controller = customFetchContext.controller.signal
      ? customFetchContext.controller
      : new AbortController();
    const signal = customFetchContext.controller.signal;
    customOptions.signal = signal;
  }

  const apiStart = Date.now();

  return oFetch(url, fetchOptions)
    .then(res => {
      const apiEnd = Date.now();

      // 成功的接口请求打印接口耗时
      if (useApiLog && $logger) {
        $logger.info(`[API Fetch Time]`, {
          api: baseURL + url,
          time: apiEnd - apiStart,
        });
      }

      return res;
    })
    .catch(err => {
      useApiLog &&
        $logger &&
        $logger.error('[OFetch Error]', {
          err,
          api: baseURL + url,
          opts: customOptions,
        });

      return Promise.reject(err);
    });
};
